import { forwardRef, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import type { Dealer } from '@cdab/scania/qpr/schema'
import { capitalizeFirstLetter, formatISODate } from '@cdab/utils'
import type { TdsDatetimeCustomEvent } from '@scania/tegel'
import {
  TdsButton,
  TdsDatetime,
  TdsMessage,
  TdsModal,
  TdsSpinner,
  TdsToast
} from '@scania/tegel-react'
import { toast } from 'react-toastify'

function formatDate(date: Date | null): string | undefined {
  if (date === null) return undefined

  return formatISODate(date).split('T')[0]
}

export function checkValidDate(
  dateString: string | undefined,
  dealerExpiryDate: Date | null,
  expiryDateOld: Date | null | undefined
): boolean {
  let limitDate = undefined

  if (expiryDateOld) {
    limitDate = expiryDateOld
  } else if (dealerExpiryDate) {
    limitDate = dealerExpiryDate
  }

  const date = new Date(dateString || '')
  if (!limitDate || Number.isNaN(date.valueOf())) return true

  date.setMinutes(date.getTimezoneOffset()) // Convert to UTC
  return date <= limitDate
}

type UseDealerModalState =
  | {
      status: 'success'
      expiryDateOld: Date | null
    }
  | {
      status: 'loading'
      expiryDateOld: undefined
    }
  | {
      status: 'error'
      expiryDateOld: undefined
    }

const useExpiryDateOld = (dealerId: number, open: boolean) => {
  const [state, setState] = useState<UseDealerModalState>({
    status: 'loading',
    expiryDateOld: undefined
  })

  useEffect(() => {
    async function getExpiryDateLimit() {
      try {
        const client = getClient()

        const expiryDateOld =
          await client.DealersService.GetExpiryDateLimitForDealer(dealerId)
        setState({
          status: 'success',
          expiryDateOld
        })
      } catch (error) {
        setState({
          status: 'error',
          expiryDateOld: undefined
        })
        console.warn(error)
      }
    }
    if (open) getExpiryDateLimit()
  }, [dealerId, open])

  return state
}

export type DealerModalProps = {
  open: boolean
  onClose: () => void
  onSubmit: (date: string) => Promise<boolean>
  dealer: Dealer
}

export const DealerModal = forwardRef<HTMLTdsModalElement, DealerModalProps>(
  ({ open, onClose, onSubmit, dealer }, ref) => {
    const { t } = useTranslation(['common', 'errors'])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [date, setDate] = useState(formatDate(dealer.expiryDate))
    const [dateError, setError] = useState<null | string>(null)
    const modalState = useExpiryDateOld(dealer.id, open)
    const isValidExpiryDate = useMemo(
      () => checkValidDate(date, dealer.expiryDate, modalState.expiryDateOld),
      [date, dealer.expiryDate, modalState.expiryDateOld]
    )

    const isSaveDisabled =
      !isValidExpiryDate ||
      isSubmitting ||
      !!dateError ||
      modalState.status !== 'success'

    const handleFormSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      if (isSaveDisabled) return

      if (isSubmitting || !dealer.expiryDate) return
      setError(null)

      if (!date) {
        setError(t('invalid-date', { ns: 'errors' }))
        return
      }

      if (!isValidExpiryDate) {
        setError(t('previous-date', { ns: 'errors' }))
        return
      }

      try {
        setIsSubmitting(true)
        const result = await onSubmit(date)

        if (!result) {
          setError(t('previous-date', { ns: 'errors' }))
        } else {
          toast(
            <TdsToast
              variant='success'
              header={t('expiry-date-updated', { ns: 'common' })}
              subheader={t('expiry-date-updated-success', { ns: 'common' })}
            />
          )
        }
      } catch (error) {
        // TODO: Log to sentry
        console.warn(error)
      } finally {
        onClose()
        setIsSubmitting(false)
      }
    }

    const onInput = async (e: TdsDatetimeCustomEvent<InputEvent>) => {
      setError(null)

      if (!e.target.value) {
        setError(t('invalid-date', { ns: 'errors' }))
        return
      }

      const isValidExpiryDate = checkValidDate(
        e.target.value,
        dealer.expiryDate,
        modalState.expiryDateOld
      )

      if (!isValidExpiryDate) {
        setError(t('previous-date', { ns: 'errors' }))
        return
      }

      setDate(e.target.value)
    }

    return (
      <TdsModal
        show={open}
        ref={ref}
        size='lg'
        actions-position='static'
        header={`${dealer.name}, ${dealer.city}`}
      >
        <div slot='body' className='tds-u-mt3'>
          {modalState.status === 'error' && (
            <TdsMessage
              header={t('dealer-modal-expiry-date', { ns: 'errors' })}
              variant='error'
            />
          )}
          {modalState.status === 'loading' && <TdsSpinner />}
          {modalState.status === 'success' && (
            <TdsDatetime
              helper={dateError || undefined}
              label={t('expiry-date')}
              type='date'
              value={date}
              state={dateError ? 'error' : 'none'}
              onTdsInput={onInput}
            />
          )}
        </div>
        <span slot='actions' className='tds-u-flex tds-u-gap2'>
          <TdsButton
            disabled={isSaveDisabled}
            text={capitalizeFirstLetter(t('save'))}
            onClick={handleFormSubmit}
          />
          <TdsButton
            disabled={isSubmitting}
            variant='secondary'
            onClick={onClose}
            text={capitalizeFirstLetter(t('close'))}
          />
        </span>
      </TdsModal>
    )
  }
)
